import type { ComfortInput, ComfortResult } from '@comforttool/comfortcore';

/**
 * Lage-risico export: alleen ingevoerde velden en zichtbare uitkomsten (geen formules of interne coëfficiënten).
 */
export function buildComfortSummaryCsv(input: ComfortInput, result: ComfortResult): string {
  const esc = (v: string | number): string => {
    const s = String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const line = (a: string, b: string, c: string) => `${esc(a)},${esc(b)},${esc(c)}`;

  const lines: string[] = [];
  lines.push(line('kop', 'ComfortTool samenvatting', 'indicatief geen officieel label'));
  lines.push(line('kop', 'engine_versie', result.engineVersion));
  lines.push(line('', '', ''));

  lines.push(line('invoer', 'postcode', input.postcode || ''));
  lines.push(line('invoer', 'vloeroppervlakte_m2', String(input.vloeroppervlakte)));
  lines.push(line('invoer', 'type_woning', input.type_woning));
  lines.push(line('', '', ''));

  lines.push(line('gebouw', 'warmtelast_kW', result.warmtelastKW.toFixed(3)));
  lines.push(line('gebouw', 'koellast_kW', result.koellastKW.toFixed(3)));
  lines.push(line('gebouw', 'piek_kW', result.pieklastKWDisplay.toFixed(3)));
  lines.push(line('gebouw', 'jaar_warmte_kWh', String(Math.round(result.jaarverbruikWarmteKWh))));
  lines.push(line('gebouw', 'jaar_koel_kWh', String(Math.round(result.jaarverbruikKoelKWh))));
  lines.push(
    line('gebouw', 'kWh_per_m2_vloer_jaar', String(result.engineInsights.energieIntensiteitKWhPerM2Jaar))
  );
  if (result.climateContext) {
    lines.push(line('gebouw', 'klimaat_label', result.climateContext.locationLabel));
  }
  lines.push(line('', '', ''));

  for (const s of result.scenarios) {
    lines.push(line('scenario', s.id, s.label));
    lines.push(line('scenario_warmte_kW', s.label, String(s.warmtelastKW)));
    lines.push(line('scenario_koel_kW', s.label, String(s.koellastKW)));
    lines.push(line('scenario_piek_kW', s.label, String(s.pieklastKW)));
  }
  lines.push(line('', '', ''));

  for (const r of result.ruimteLasten) {
    lines.push(line('ruimte', 'naam', r.naam));
    lines.push(line('ruimte', `${r.naam}_vloer_m2`, String(r.vloeroppervlakte_m2)));
    lines.push(line('ruimte', `${r.naam}_warmte_kW`, (r.warmte_W / 1000).toFixed(3)));
    lines.push(line('ruimte', `${r.naam}_koel_kW`, (r.koel_W / 1000).toFixed(3)));
    lines.push(line('ruimte', `${r.naam}_jaar_warmte_kWh`, String(Math.round(r.jaar_warmte_kWh))));
    lines.push(line('ruimte', `${r.naam}_jaar_koel_kWh`, String(Math.round(r.jaar_koel_kWh))));
  }

  return lines.join('\r\n');
}

export function downloadComfortSummaryCsv(
  input: ComfortInput,
  result: ComfortResult,
  filename = 'comforttool-samenvatting.csv'
) {
  const csv = buildComfortSummaryCsv(input, result);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
